import React, { createContext, useContext, useEffect, useCallback, ReactNode } from 'react';
import { useLocalStorage } from '../hooks/useLocalStorage';
import {
  calculateOverallCompletion,
  checkForNewBadges,
  ProgressMetrics,
  XP_REWARDS
} from '../utils/progressCalculator';

export interface UserProgress {
  experiencePoints: number;
  sectionsVisited: string[];
  courtZonesClicked: string[];
  totalCourtZones: number;
  quizzesCompleted: string[];
  perfectQuizzes: string[];
  quizScores: Record<string, number>;
  gamesPlayed: string[];
  aiQuestionsAsked: number;
  unlockedBadges: string[];
  newBadges: string[];
  sectionProgress: {
    court: number;
    rules: number;
    scoring: number;
    games: number;
    aiRef: number;
  };
  overallCompletion: number;
}

const defaultProgress: UserProgress = {
  experiencePoints: 0,
  sectionsVisited: [],
  courtZonesClicked: [],
  totalCourtZones: 10,
  quizzesCompleted: [],
  perfectQuizzes: [],
  quizScores: {},
  gamesPlayed: [],
  aiQuestionsAsked: 0,
  unlockedBadges: [],
  newBadges: [],
  sectionProgress: {
    court: 0,
    rules: 0,
    scoring: 0,
    games: 0,
    aiRef: 0,
  },
  overallCompletion: 0,
};

interface ProgressContextType {
  progress: UserProgress;
  awardXP: (amount: number, reason?: string) => void;
  visitSection: (sectionId: string) => void;
  clickCourtZone: (zoneId: string) => void;
  setTotalCourtZones: (total: number) => void;
  completeQuiz: (quizId: string, score: number, totalQuestions: number) => void;
  playGame: (gameId: string, score: number) => void;
  askAIQuestion: () => void;
  clearNewBadges: () => void;
  resetProgress: () => void;
}

const ProgressContext = createContext<ProgressContextType | undefined>(undefined);

export const ProgressProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [progress, setProgress] = useLocalStorage<UserProgress>('padel-progress-v1', defaultProgress);

  useEffect(() => {
    const totalCourtZones = Math.max(progress.totalCourtZones, 1);
    const metrics: ProgressMetrics = {
      courtExploration: (progress.courtZonesClicked.length / totalCourtZones) * 100,
      rulesCompleted: progress.sectionsVisited.includes('rules') ? 100 : 0,
      scoringPracticed: progress.sectionsVisited.includes('scoring') ? 100 : 0,
      gamesPlayed: (progress.gamesPlayed.length / 4) * 100,
      aiInteracted: Math.min((progress.aiQuestionsAsked / 10) * 100, 100),
    };
    
    const overallCompletion = calculateOverallCompletion(metrics);

    setProgress(prev => ({
      ...prev,
      sectionProgress: {
        court: Math.floor(metrics.courtExploration),
        rules: Math.floor(metrics.rulesCompleted),
        scoring: Math.floor(metrics.scoringPracticed),
        games: Math.floor(metrics.gamesPlayed),
        aiRef: Math.floor(metrics.aiInteracted),
      },
      overallCompletion,
    }));
  }, [progress.experiencePoints, progress.courtZonesClicked.length, progress.sectionsVisited.length, progress.gamesPlayed.length, progress.aiQuestionsAsked]);

  useEffect(() => {
    const newBadges = checkForNewBadges(
      {
        sectionsVisited: new Set(progress.sectionsVisited),
        courtZonesClicked: new Set(progress.courtZonesClicked),
        totalCourtZones: progress.totalCourtZones,
        quizzesCompleted: progress.quizzesCompleted,
        perfectQuizzes: progress.perfectQuizzes,
        gamesPlayed: progress.gamesPlayed,
        aiQuestionsAsked: progress.aiQuestionsAsked,
        overallCompletion: progress.overallCompletion,
        totalBadges: progress.unlockedBadges.length,
      },
      progress.unlockedBadges
    );

    if (newBadges.length > 0) {
      setProgress(prev => ({
        ...prev,
        unlockedBadges: [...prev.unlockedBadges, ...newBadges.map(b => b.id)],
        newBadges: [...prev.newBadges, ...newBadges.map(b => b.id)],
        experiencePoints: prev.experiencePoints + newBadges.reduce((sum, b) => sum + b.xpReward, 0),
      }));
    }
  }, [
    progress.sectionsVisited.length,
    progress.courtZonesClicked.length,
    progress.quizzesCompleted.length,
    progress.perfectQuizzes.length,
    progress.gamesPlayed.length,
    progress.aiQuestionsAsked,
    progress.overallCompletion,
  ]);

  const awardXP = useCallback((amount: number, _reason?: string) => {
    setProgress(prev => ({
      ...prev,
      experiencePoints: prev.experiencePoints + amount,
    }));
  }, []);

  const visitSection = useCallback((sectionId: string) => {
    setProgress(prev => {
      if (prev.sectionsVisited.includes(sectionId)) {
        return prev;
      }
      return {
        ...prev,
        sectionsVisited: [...prev.sectionsVisited, sectionId],
        experiencePoints: prev.experiencePoints + XP_REWARDS.VISIT_SECTION,
      };
    });
  }, []);

  const clickCourtZone = useCallback((zoneId: string) => {
    setProgress(prev => {
      if (prev.courtZonesClicked.includes(zoneId)) {
        return prev;
      }
      return {
        ...prev,
        courtZonesClicked: [...prev.courtZonesClicked, zoneId],
        experiencePoints: prev.experiencePoints + XP_REWARDS.CLICK_COURT_ZONE,
      };
    });
  }, []);

  const setTotalCourtZones = useCallback((total: number) => {
    setProgress(prev => ({
      ...prev,
      totalCourtZones: total,
    }));
  }, []);

  const completeQuiz = useCallback((quizId: string, score: number, totalQuestions: number) => {
    const validScore = Math.max(0, Math.min(score, totalQuestions));
    const validTotal = Math.max(1, totalQuestions);

    setProgress(prev => {
      const isPerfect = validScore === validTotal;
      const isNewCompletion = !prev.quizzesCompleted.includes(quizId);
      
      const baseXP = isNewCompletion ? XP_REWARDS.COMPLETE_QUIZ : 0;
      const perfectBonus = isPerfect ? XP_REWARDS.PERFECT_QUIZ_BONUS : 0;
      const xpGained = baseXP + (validScore * XP_REWARDS.QUIZ_CORRECT_ANSWER) + perfectBonus;

      return {
        ...prev,
        quizzesCompleted: isNewCompletion ? [...prev.quizzesCompleted, quizId] : prev.quizzesCompleted,
        perfectQuizzes: isPerfect && !prev.perfectQuizzes.includes(quizId)
          ? [...prev.perfectQuizzes, quizId]
          : prev.perfectQuizzes,
        quizScores: {
          ...prev.quizScores,
          [quizId]: Math.max(prev.quizScores[quizId] || 0, validScore),
        },
        experiencePoints: prev.experiencePoints + xpGained,
      };
    });
  }, []);

  const playGame = useCallback((gameId: string, _score: number) => {
    setProgress(prev => {
      const isNewGame = !prev.gamesPlayed.includes(gameId);
      const baseXP = isNewGame ? XP_REWARDS.PLAY_GAME : 0;
      const xpGained = baseXP;

      return {
        ...prev,
        gamesPlayed: isNewGame ? [...prev.gamesPlayed, gameId] : prev.gamesPlayed,
        experiencePoints: prev.experiencePoints + xpGained,
      };
    });
  }, []);

  const askAIQuestion = useCallback(() => {
    setProgress(prev => {
      const newCount = prev.aiQuestionsAsked + 1;
      let bonusXP = 0;
      
      if (newCount === 10) bonusXP = XP_REWARDS.AI_MILESTONE_10;
      if (newCount === 25) bonusXP = XP_REWARDS.AI_MILESTONE_25;
      
      return {
        ...prev,
        aiQuestionsAsked: newCount,
        experiencePoints: prev.experiencePoints + XP_REWARDS.ASK_AI_QUESTION + bonusXP,
      };
    });
  }, []);

  const clearNewBadges = useCallback(() => {
    setProgress(prev => ({
      ...prev,
      newBadges: [],
    }));
  }, []);

  const resetProgress = useCallback(() => {
    setProgress(defaultProgress);
  }, []);

  const value: ProgressContextType = {
    progress,
    awardXP,
    visitSection,
    clickCourtZone,
    setTotalCourtZones,
    completeQuiz,
    playGame,
    askAIQuestion,
    clearNewBadges,
    resetProgress,
  };

  return (
    <ProgressContext.Provider value={value}>
      {children}
    </ProgressContext.Provider>
  );
};

export const useProgress = (): ProgressContextType => {
  const context = useContext(ProgressContext);
  if (!context) {
    throw new Error('useProgress must be used within ProgressProvider');
  }
  return context;
};
